import type { ITheme } from '@xterm/xterm';

// resolve any CSS color string (hsl, oklch, rgb, hex, etc.) to #rrggbb or #rrggbbaa
const toHex = (() => {
	const canvas = document.createElement('canvas');
	canvas.width = 1;
	canvas.height = 1;
	const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
	return (color: string): string => {
		// handle transparent / empty
		const trimmed = color.trim();
		if (!trimmed || trimmed === 'transparent') return '#00000000';
		// draw a single pixel and read it back — handles every CSS color format
		ctx.clearRect(0, 0, 1, 1);
		ctx.fillStyle = trimmed;
		ctx.fillRect(0, 0, 1, 1);
		const d = ctx.getImageData(0, 0, 1, 1).data;
		const hex = (v: number) => v.toString(16).padStart(2, '0');
		if (d[3] === 255) return `#${hex(d[0]!)}${hex(d[1]!)}${hex(d[2]!)}`;
		return `#${hex(d[0]!)}${hex(d[1]!)}${hex(d[2]!)}${hex(d[3]!)}`;
	};
})();

// reads a CSS custom property from :root, resolved to hex (#rrggbb)
export const css = (name: string): string =>
	toHex(getComputedStyle(document.documentElement).getPropertyValue(name).trim());

// reads the monospace font-family from tailwind's --font-mono variable
export const monoFont = (): string =>
	getComputedStyle(document.documentElement).getPropertyValue('--font-mono').trim() || 'monospace';

// reads the monospace font size from tailwind's --font-mono-size variable
export const monoFontSize = (): number =>
	parseInt(
		getComputedStyle(document.documentElement).getPropertyValue('--font-mono-size').trim(),
		10,
	) || 13;

// build an xterm ITheme from CSS custom properties
export const terminalTheme = (): ITheme => ({
	background: css('--color-editor'),
	foreground: css('--color-editor-foreground'),
	cursor: css('--color-cursor'),
	cursorAccent: css('--color-editor'),
	selectionBackground: css('--color-selection'),
	black: css('--color-ansi-black'),
	red: css('--color-ansi-red'),
	green: css('--color-ansi-green'),
	yellow: css('--color-ansi-yellow'),
	blue: css('--color-ansi-blue'),
	magenta: css('--color-ansi-magenta'),
	cyan: css('--color-ansi-cyan'),
	white: css('--color-ansi-white'),
	brightBlack: css('--color-ansi-bright-black'),
	brightRed: css('--color-ansi-bright-red'),
	brightGreen: css('--color-ansi-bright-green'),
	brightYellow: css('--color-ansi-bright-yellow'),
	brightBlue: css('--color-ansi-bright-blue'),
	brightMagenta: css('--color-ansi-bright-magenta'),
	brightCyan: css('--color-ansi-bright-cyan'),
	brightWhite: css('--color-ansi-bright-white'),
});
